/**
 * NAVIGATION DRAWER HYDRATION
 *
 * The header ships four navigation drawers — the mobile sidebar plus one per mega-menu block —
 * and every one of them is invisible until the shopper opens it. Their markup used to be parsed
 * as live DOM on every page load: ~230 KB, ~3400 nodes and (before the thumbnail setting was
 * honoured) ~420 image elements, all laid out and style-resolved before first paint for content
 * nobody had asked to see.
 *
 * The panels now live inside `<template data-nav-panel>`, which the browser parses inert: nodes
 * exist in a detached fragment with no layout, no style resolution and no image loading. This
 * file puts them back, on whichever happens first:
 *
 *   1. A click on a control that opens the drawer. Intercepted in the capture phase on
 *      `document`, which runs before theme.js's delegated listener on `document.body`, so the
 *      panel is real DOM by the time DialogElement sets the `open` attribute. That ordering
 *      matters: NavigationDrawer.reinitializeDrawer() dereferences `.panel:first-child` without
 *      a null check, so opening an unhydrated drawer would throw.
 *
 *   2. Intent: pointerdown / pointerover / focusin on a menu control, so the drawer is built
 *      before the click lands and the first open stays instant.
 *
 *   3. Crawlers only: an idle callback after `load`. `<template>` content is invisible to
 *      crawlers, so they still get the ~435 nav links in the rendered DOM. Shoppers no longer
 *      pay for it: building all four drawers on idle was one ~3300-node block on every page.
 *
 * Shopify's theme editor gets hydrated immediately — section and block selection drive
 * `show()` directly and can fire before any idle callback.
 */

(() => {
  const TEMPLATE_SELECTOR = 'template[data-nav-panel]';

  /** Replace a panel template with its live content. Returns true if anything was hydrated. */
  const hydrateTemplate = (template) => {
    if (!template || !template.isConnected) return false;

    // Inserting the fragment moves its children into the document, where custom elements
    // upgrade and the nodes are adopted into the main document automatically.
    template.replaceWith(template.content);

    // Panels contain localization selectors whose flag stylesheet <link> is attached lazily by
    // the media-hydration block below. Announce the insertion so a panel hydrated *after* the
    // idle flag swap still gets its stylesheet switched in.
    document.dispatchEvent(new CustomEvent('navpanel:hydrated'));
    return true;
  };

  const hydrateDrawer = (drawer) => {
    if (!drawer) return false;

    let hydrated = false;
    // Only this drawer's own templates — panels are never nested, but scoping keeps a future
    // nested drawer from being hydrated by a click on its parent.
    drawer.querySelectorAll(TEMPLATE_SELECTOR).forEach((template) => {
      hydrated = hydrateTemplate(template) || hydrated;
    });
    return hydrated;
  };

  const hydrateAll = (root = document) => {
    root.querySelectorAll(TEMPLATE_SELECTOR).forEach(hydrateTemplate);
  };

  /**
   * A drawer opens when something with `aria-controls` pointing at it is activated. Keyboard
   * activation of a <button> dispatches a click too, so this covers pointer and keyboard alike.
   */
  document.addEventListener(
    'click',
    (event) => {
      const control = event.target instanceof Element ? event.target.closest('[aria-controls]') : null;
      if (!control) return;

      const controlled = document.getElementById(control.getAttribute('aria-controls'));
      if (controlled && controlled.tagName === 'NAVIGATION-DRAWER') {
        hydrateDrawer(controlled);
      }
    },
    true
  );

  /**
   * Intent: a finger landing on (or a pointer/focus reaching) a menu control builds that drawer
   * before the click arrives, so the first open is still instant. Building all four on idle
   * cost every page view ~3300 nodes and ~420 image elements in one main-thread block (127 ms
   * on a fast desktop, several hundred on a phone) for menus most visitors never open.
   */
  const hydrateFromControl = (event) => {
    const control = event.target instanceof Element ? event.target.closest('[aria-controls]') : null;
    if (!control) return;
    const controlled = document.getElementById(control.getAttribute('aria-controls'));
    if (controlled && controlled.tagName === 'NAVIGATION-DRAWER') hydrateDrawer(controlled);
  };

  ['pointerdown', 'pointerover', 'focusin'].forEach((type) => {
    document.addEventListener(type, hydrateFromControl, { capture: true, passive: true });
  });

  const whenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 1200);
    }
  };

  const hydrateWhenIdle = () => whenIdle(() => hydrateAll());

  // Crawlers render the page but never open a menu: they still get every panel on idle, so the
  // ~435 nav links stay in the rendered DOM for indexing. Same markup, only the timing differs.
  const isCrawler = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit/i.test(navigator.userAgent || '');

  if (window.Shopify && window.Shopify.designMode) {
    // The editor drives show() from shopify:section:select / shopify:block:select, which can
    // arrive before an idle callback would, and re-renders the header on every settings change.
    hydrateAll();
    document.addEventListener('shopify:section:load', (event) => hydrateAll(event.target));
  } else if (isCrawler) {
    if (document.readyState === 'complete') {
      hydrateWhenIdle();
    } else {
      window.addEventListener('load', hydrateWhenIdle, { once: true });
    }
  }
})();

// --- media hydration (product card hover images, video posters, flag sprite) ---
//
// Deferred media that must not cost bandwidth up front:
//
//   1. Product-card hover images. product-card.liquid renders the secondary image with
//      data-src/data-srcset only — a real src would be fetched by native lazy-loading for every
//      card in the viewport, because the desktop CSS keeps the element at `opacity: 0;
//      display: block`. The attributes are promoted on the card's first pointerover (or focusin
//      for keyboard), so the image is only requested when someone actually shows intent. The
//      listener is delegated, so cards injected later (facet filtering, quick-buy, search
//      drawer) are covered too.
//
//   2. Video-slider posters. A <video poster> can't lazy-load natively, so up to 20 poster
//      images used to download eagerly on product pages. video-slider.liquid ships the URL in
//      data-poster and an IntersectionObserver sets it just before the card scrolls into view.
//
//   3. The country-flag sprite (~96 KB retina CSS sprite). localization-selector.liquid emits
//      its stylesheet <link> with data-href instead of href, and the href is attached here on
//      idle after `load`. Several selector instances can render on one page (header, footer,
//      nav drawer), so every matching link is switched in.
(() => {
  // 1. Product-card hover images.
  const hydrateCardMedia = (event) => {
    const card = event.target instanceof Element ? event.target.closest('product-card') : null;
    if (!card) return;

    // After the first hydration the data attributes are gone, so repeated pointerover events
    // on the same card only cost this (empty) query.
    card.querySelectorAll('img[data-src], img[data-srcset]').forEach((image) => {
      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        image.removeAttribute('data-srcset');
      }

      if (image.dataset.src) {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      }
    });
  };

  document.addEventListener('pointerover', hydrateCardMedia, { capture: true, passive: true });
  document.addEventListener('focusin', hydrateCardMedia, true);

  // 2. Video posters.
  const hydratePoster = (video) => {
    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      video.removeAttribute('data-poster');
    }
  };

  const observePosters = () => {
    const videos = document.querySelectorAll('video[data-poster]');
    if (videos.length === 0) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            hydratePoster(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '200px' }
      );

      videos.forEach((video) => observer.observe(video));
    } else {
      videos.forEach(hydratePoster);
    }
  };

  // This script is deferred, so the DOM is parsed by the time it runs and the sliders'
  // <video> elements exist.
  observePosters();

  // 3. Flag sprite stylesheet.
  let flagsAttached = false;

  const attachFlagStylesheets = () => {
    flagsAttached = true;
    document.querySelectorAll('link[data-flags-css]').forEach((link) => {
      if (!link.href && link.dataset.href) link.href = link.dataset.href;
    });
  };

  // Nav panels (which render localization selectors of their own) can hydrate after the idle
  // swap has already run — re-attach for any link that arrived late. Before the idle swap this
  // is a no-op so the sprite is never fetched early.
  document.addEventListener('navpanel:hydrated', () => {
    if (flagsAttached) attachFlagStylesheets();
  });

  const whenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 4000 });
    } else {
      setTimeout(callback, 2000);
    }
  };

  const flagsWhenIdle = () => whenIdle(attachFlagStylesheets);

  if (document.readyState === 'complete') {
    flagsWhenIdle();
  } else {
    window.addEventListener('load', flagsWhenIdle, { once: true });
  }
})();

// --- menu thumbnails ---
//
// The panels render empty [data-nav-img="<link url>"] slots; the pictures come from
// sections/nav-images.liquid via the Section Rendering API.
//
// Resolving those images is expensive server-side — each one is the featured image of a
// collection's first product, ~200 of them, measured at ~640ms of render time on the home page.
// Paid inline it was on the critical path of every page, for a menu most visitors never open.
// Fetched here it costs nothing before first paint: one background request on idle after load,
// reused from sessionStorage for the rest of the session.
//
// The slot is a fixed-size box with its own background, so filling it late shifts no layout.
(() => {
  const CACHE_KEY = 'sneak:nav-images:v1';
  const SECTION_ID = 'nav-images';

  let map = null;
  let inFlight = null;

  const rootUrl = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // sessionStorage throws in private-mode Safari and when the quota is full; a missing cache
  // only costs an extra fetch, so every access degrades to "no cache" rather than failing.
  const readCache = () => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  };

  const writeCache = (value) => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
    } catch (error) {
      /* cache is an optimization, never a requirement */
    }
  };

  const fillSlots = () => {
    if (!map) return;

    document.querySelectorAll('[data-nav-img]').forEach((slot) => {
      const src = map[slot.dataset.navImg];

      // Claim the slot either way: a link with no image must not be re-examined on every
      // later fill (panels hydrate at different times).
      slot.removeAttribute('data-nav-img');
      if (!src) {
        slot.classList.add('menu-item-image-wrapper--empty');
        return;
      }

      const image = document.createElement('img');
      image.className = 'menu-item-image';
      image.width = 120;
      image.height = 120;
      image.loading = 'lazy';
      image.decoding = 'async';
      // Decorative: every slot sits next to the link's own text label.
      image.alt = '';
      image.src = src;
      slot.appendChild(image);
    });
  };

  const loadMap = () => {
    if (map) {
      fillSlots();
      return Promise.resolve();
    }

    const cached = readCache();
    if (cached) {
      map = cached;
      fillSlots();
      return Promise.resolve();
    }

    if (inFlight) return inFlight;

    inFlight = fetch(`${rootUrl()}?section_id=${SECTION_ID}`, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`nav-images fetch failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        // The section is JSON inside a <script>, so it is parsed, never injected as markup.
        const node = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('script[data-nav-images]');

        map = node ? JSON.parse(node.textContent) : {};
        writeCache(map);
        fillSlots();
      })
      .catch(() => {
        // Thumbnails are decoration. On failure the slots stay empty boxes and the menu works.
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  };

  // A panel hydrates on drawer-open intent as well as on idle, so this both fills the slots of a
  // panel that arrived after the map and pulls the map forward when someone opens the menu before
  // the idle callback has run.
  // Only when a panel is built (menu intent, or a crawler's idle build). The section request costs
  // ~640 ms of server render and used to run on idle on every first page of every session.
  document.addEventListener('navpanel:hydrated', loadMap);
})();
